import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Bell, Check, AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { api, fmtDateRel } from "../lib/api";
import { useI18n } from "../lib/i18n";

const ICONS = { danger: AlertCircle, warning: AlertTriangle, info: Info, success: Check };
const COLORS = {
    danger: "bg-rose-50 text-rose-600 border-rose-200",
    warning: "bg-amber-50 text-amber-600 border-amber-200",
    info: "bg-blue-50 text-blue-600 border-blue-200",
    success: "bg-emerald-50 text-emerald-600 border-emerald-200",
};

export default function NotificationBell({ variant = "light", testid = "notif-bell" }) {
    const { t } = useI18n();
    const [items, setItems] = useState([]);
    const [unread, setUnread] = useState(0);
    const [open, setOpen] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get("/notifications");
            setItems(r.data.items || []);
            setUnread(r.data.unread_count || 0);
            // Fire browser notifications for new items if permission granted
            if (typeof window !== "undefined" && "Notification" in window && Notification.permission === "granted") {
                const seenKey = "opc_seen_notifs";
                let seen;
                try { seen = JSON.parse(localStorage.getItem(seenKey) || "[]"); } catch { seen = []; }
                const fresh = (r.data.items || []).filter((i) => !i.read && !seen.includes(i.id));
                fresh.slice(0, 3).forEach((i) => {
                    try {
                        new Notification(i.title, { body: i.body, icon: "https://customer-assets.emergentagent.com/job_bd924cda-aeef-4b77-b265-df028a44b434/artifacts/1osskqe6_3E5BB8AE-DADE-4A70-A667-D48D088B8602_1.PNG" });
                    } catch {}
                });
                const newSeen = [...seen, ...fresh.map((i) => i.id)].slice(-50);
                localStorage.setItem(seenKey, JSON.stringify(newSeen));
            }
        } catch {}
    }, []);

    useEffect(() => {
        refresh();
        const id = setInterval(refresh, 30000); // poll every 30s
        return () => clearInterval(id);
    }, [refresh]);

    const requestPerm = async () => {
        if (typeof window === "undefined" || !("Notification" in window)) return;
        if (Notification.permission === "default") {
            try {
                await Notification.requestPermission();
            } catch {}
        }
    };

    const markAll = async () => {
        await api.post("/notifications/mark-all-read");
        refresh();
    };

    const markOne = async (id) => {
        await api.post(`/notifications/${id}/read`);
        refresh();
    };

    const isDark = variant === "dark";

    return (
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (v) requestPerm(); }}>
            <PopoverTrigger asChild>
                <button
                    data-testid={testid}
                    className={`relative size-10 rounded-full ${isDark ? "bg-white/15 hover:bg-white/20 text-white" : "hover:bg-slate-100 text-slate-700"} flex items-center justify-center tap-feedback transition`}
                >
                    <Bell size={18} />
                    {unread > 0 && (
                        <span
                            className="absolute top-1.5 right-1.5 min-w-4 h-4 px-1 rounded-full bg-[#FF0055] text-white text-[10px] font-bold flex items-center justify-center"
                            data-testid="notif-unread-count"
                        >
                            {unread > 9 ? "9+" : unread}
                        </span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[360px] p-0 rounded-2xl border border-slate-200 shadow-xl" align="end" data-testid="notif-popover">
                <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <h3 className="font-display font-semibold text-[#0A1930]">{t("common.notifications")}</h3>
                    {unread > 0 && (
                        <button onClick={markAll} className="text-xs font-bold text-[#0066FF] hover:underline" data-testid="notif-mark-all">
                            {t("common.mark_all_read")}
                        </button>
                    )}
                </div>
                <div className="max-h-[400px] overflow-y-auto admin-scroll">
                    {items.length === 0 && (
                        <div className="p-8 text-center text-sm text-slate-500">
                            <Check className="size-10 mx-auto text-emerald-400 mb-2" />
                            {t("common.no_notifications")}
                        </div>
                    )}
                    {items.map((n) => {
                        const Icon = ICONS[n.severity] || Info;
                        const cls = COLORS[n.severity] || COLORS.info;
                        return (
                            <Link
                                to={n.action_url || "#"}
                                key={n.id}
                                onClick={() => { setOpen(false); markOne(n.id); }}
                                data-testid={`notif-item-${n.id}`}
                                className={`block px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition ${!n.read ? "bg-blue-50/40" : ""}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className={`size-9 rounded-lg flex items-center justify-center border ${cls} flex-shrink-0`}>
                                        <Icon size={16} />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                            <span className="font-semibold text-sm text-[#0A1930] line-clamp-2">{n.title}</span>
                                            {!n.read && <span className="size-2 rounded-full bg-[#0066FF] mt-1.5 flex-shrink-0" />}
                                        </div>
                                        <p className="text-xs text-slate-600 mt-0.5 line-clamp-2">{n.body}</p>
                                        <p className="text-[10px] text-slate-400 mt-1">{fmtDateRel(n.created_at)}</p>
                                    </div>
                                </div>
                            </Link>
                        );
                    })}
                </div>
                {items.length > 0 && (
                    <div className="px-4 py-2 border-t border-slate-100 text-center">
                        <Link
                            to="/consumer/notifications"
                            onClick={() => setOpen(false)}
                            className="text-xs font-bold text-[#0066FF] hover:underline"
                            data-testid="notif-view-all"
                        >
                            View all
                        </Link>
                    </div>
                )}
            </PopoverContent>
        </Popover>
    );
}
